import { CommonModule, DOCUMENT, isPlatformServer } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  HostListener,
  inject,
  input,
  OnDestroy,
  PLATFORM_ID,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';

import { MediaComponent } from '@app/components/media/media.component';
import { TextComponent } from '@app/components/text/text.component';
import { TrapFocusDirective } from '@app/directives/trap-focus.directive';
import { Media } from '@app/models/media.model';
import {
  MobiUiCarouselComponent,
  MobiUiIconComponent,
} from '@mobi/rwc-ui-components-ng-jslib';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ApiService } from '@app/services/api.service';

@Component({
  selector: 'mobi-gallery',
  imports: [
    CommonModule,
    TrapFocusDirective,
    MediaComponent,
    TextComponent,
    MobiUiIconComponent,
    MobiUiCarouselComponent,
  ],
  templateUrl: './gallery.component.html',
  styleUrl: './gallery.component.scss',
})
export class GalleryComponent implements OnDestroy {
  readonly images = input.required<readonly Media[]>();
  readonly type = input<'gallery' | 'carousel'>('gallery');
  readonly carousel = viewChild<MobiUiCarouselComponent>('carousel');

  isOverlayOpen = signal(false);
  readonly overlayTemplate = viewChild<TemplateRef<any>>('overlayTemplate');
  portal?: TemplatePortal;

  #selectedIndex = signal(0);
  #document = inject(DOCUMENT);
  #platformId = inject(PLATFORM_ID);
  #viewContainerRef = inject(ViewContainerRef);
  #overlay = inject(Overlay);
  #overlayRef: OverlayRef | null = null;
  #api = inject(ApiService);

  selectedIndex = computed<number>(() => {
    if (this.carousel()) return this.carousel()!.selectedIndex();
    return this.#selectedIndex();
  });

  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isOverlayOpen()) {
      this.closeOverlay();
    }
  }

  #afterRender = afterNextRender({
    write: () => {
      if (this.#overlayRef || !this.overlayTemplate()) return;

      this.#overlayRef = this.#overlay.create();
      (
        this.#overlayRef.hostElement.closest('.cdk-overlay-container') as any
      ).style.zIndex = '150';
      this.#overlayRef.attach(
        new TemplatePortal(this.overlayTemplate()!, this.#viewContainerRef)
      );
    },
  });

  ngOnDestroy() {
    if (isPlatformServer(this.#platformId)) return;
    const hostElem = this.#overlayRef?.hostElement.closest(
      '.cdk-overlay-container'
    ) as any;
    hostElem.removeAttribute('style');
    this.#overlayRef?.dispose();
  }

  openOverlay(index: number) {
    this.#document.documentElement.classList.add('no-scroll');
    this.isOverlayOpen.set(true);
    if (this.carousel()) {
      this.carousel()!.selectedIndex.set(index);
    } else {
      this.#selectedIndex.set(index);
    }
    this.#document.getElementById('overlayContent')?.focus();
  }

  closeOverlay() {
    this.#document.documentElement.classList.remove('no-scroll');
    this.isOverlayOpen.set(false);
  }

  downloadImage(media: Media) {
    let url = '';
    if (media.image) {
      const rokkaHash = media.image?.entity?.rokka?.hash;
      if (rokkaHash && media.image?.entity?.filename) {
        const rokkaUrl = this.#api.rokkaUrl;
        url = rokkaUrl.concat(rokkaHash, '/', media.image?.entity?.filename);
      }
    }
    return url;
  }

  next() {
    if (this.carousel()) {
      this.carousel()?.next();
    } else {
      const next =
        this.#selectedIndex() < this.images().length - 1 ? this.#selectedIndex() + 1 : 0;
      this.#selectedIndex.set(next);
    }
  }

  prev() {
    if (this.carousel()) {
      this.carousel()?.prev();
    } else {
      const prev =
        this.#selectedIndex() > 0 ? this.#selectedIndex() - 1 : this.images().length - 1;
      this.#selectedIndex.set(prev);
    }
  }
}
